import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './household-size.reducer';

export const HouseholdSize = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const householdSizeList = useAppSelector(state => state.householdSize.entities);
  const loading = useAppSelector(state => state.householdSize.loading);
  const totalItems = useAppSelector(state => state.householdSize.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="household-size-heading" data-cy="HouseholdSizeHeading">
        <Translate contentKey="apiLocationApp.householdSize.home.title">Household Sizes</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.householdSize.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/household-size/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.householdSize.home.createLabel">Create new Household Size</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {householdSizeList && householdSizeList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.householdSize.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.householdSize.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.householdSize.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.householdSize.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.householdSize.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllHouseholds')}>
                  <Translate contentKey="apiLocationApp.householdSize.totalAllHouseholds">Total All Households</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllHouseholds')} />
                </th>
                <th className="hand" onClick={sort('onePerson')}>
                  <Translate contentKey="apiLocationApp.householdSize.onePerson">One Person</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('onePerson')} />
                </th>
                <th className="hand" onClick={sort('twoPeople')}>
                  <Translate contentKey="apiLocationApp.householdSize.twoPeople">Two People</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('twoPeople')} />
                </th>
                <th className="hand" onClick={sort('threePeople')}>
                  <Translate contentKey="apiLocationApp.householdSize.threePeople">Three People</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('threePeople')} />
                </th>
                <th className="hand" onClick={sort('fourPeople')}>
                  <Translate contentKey="apiLocationApp.householdSize.fourPeople">Four People</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('fourPeople')} />
                </th>
                <th className="hand" onClick={sort('fivePeople')}>
                  <Translate contentKey="apiLocationApp.householdSize.fivePeople">Five People</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('fivePeople')} />
                </th>
                <th className="hand" onClick={sort('sixPeople')}>
                  <Translate contentKey="apiLocationApp.householdSize.sixPeople">Six People</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sixPeople')} />
                </th>
                <th className="hand" onClick={sort('sevenPeople')}>
                  <Translate contentKey="apiLocationApp.householdSize.sevenPeople">Seven People</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sevenPeople')} />
                </th>
                <th className="hand" onClick={sort('eightOrMorePeople')}>
                  <Translate contentKey="apiLocationApp.householdSize.eightOrMorePeople">Eight Or More People</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('eightOrMorePeople')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {householdSizeList.map((householdSize, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/household-size/${householdSize.id}`} color="link" size="sm">
                      {householdSize.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${householdSize.geographicLevel}`} />
                  </td>
                  <td>{householdSize.date}</td>
                  <td>{householdSize.geography}</td>
                  <td>{householdSize.geographyCode}</td>
                  <td>{householdSize.totalAllHouseholds}</td>
                  <td>{householdSize.onePerson}</td>
                  <td>{householdSize.twoPeople}</td>
                  <td>{householdSize.threePeople}</td>
                  <td>{householdSize.fourPeople}</td>
                  <td>{householdSize.fivePeople}</td>
                  <td>{householdSize.sixPeople}</td>
                  <td>{householdSize.sevenPeople}</td>
                  <td>{householdSize.eightOrMorePeople}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/household-size/${householdSize.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/household-size/${householdSize.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/household-size/${householdSize.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.householdSize.home.notFound">No Household Sizes found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={householdSizeList && householdSizeList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default HouseholdSize;
